import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import PasswordTextField from '../components/common/PasswordTextField';
import { API_ENDPOINTS } from '../config/api';
import { useAuth } from '../contexts/AuthContext';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const { login, user, loading, error } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Reason: honor ?next= so users return to their original destination (e.g. /join/<secret>) after login.
  const getSafeNext = React.useCallback((): string | null => {
    const params = new URLSearchParams(location.search);
    const raw = params.get('next');
    if (!raw) return null;
    // Only allow same-origin relative paths to prevent open-redirect.
    if (!raw.startsWith('/') || raw.startsWith('//')) return null;
    if (raw.startsWith('/login') || raw.startsWith('/register')) return null;
    return raw;
  }, [location.search]);

  // Forgot password state
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState<string | null>(null);
  const [forgotError, setForgotError] = useState<string | null>(null);

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      // Reason: enforce consent gate for authenticated users missing required consent.
      const next = getSafeNext();
      const target = user.consent_required
        ? '/consent'
        : user.provider_terms_required
          ? '/provider-consent'
          : (next || '/home');
      navigate(target);
    }
  }, [user, navigate, getSafeNext]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    if (!email || !password) {
      setLocalError('Please fill in all fields');
      return;
    }

    try {
      // Normalize email to lowercase to ensure case-insensitive login
      const normalizedEmail = email.toLowerCase().trim();
      const loggedInUser = await login(normalizedEmail, password);

      if (loggedInUser.consent_required) {
        navigate('/consent');
        return;
      }
      if (loggedInUser.provider_terms_required) {
        navigate('/provider-consent');
        return;
      }

      const next = getSafeNext();
      navigate(next || '/home');
    } catch (error: any) {
      setLocalError(error.message);
    }
  };

  const handleForgotPassword = async (): Promise<void> => {
    if (!forgotEmail.trim()) {
      setForgotError('Please enter your email address');
      return;
    }
    setForgotLoading(true);
    setForgotError(null);
    setForgotSuccess(null);
    try {
      const response = await axios.post(
        API_ENDPOINTS.AUTH.FORGOT_PASSWORD,
        { email: forgotEmail.trim().toLowerCase() },
      );
      setForgotSuccess(response.data.message);
    } catch (err: any) {
      setForgotError(
        err.response?.data?.error || 'Something went wrong. Please try again.',
      );
    } finally {
      setForgotLoading(false);
    }
  };

  const handleForgotClose = (): void => {
    setForgotOpen(false);
    setForgotEmail('');
    setForgotError(null);
    setForgotSuccess(null);
  };

  const displayError = localError || error;

  return (
    <Container
      component="main"
      maxWidth="xs"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        py: { xs: 6, sm: 10 },
      }}
    >
      <Box sx={{ width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 4,
          }}
        >
          <Typography
            component="h1"
            sx={{
              fontFamily:
                'Poppins, Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
              fontWeight: 700,
              fontSize: { xs: 44, sm: 50 },
              lineHeight: 1.3,
              letterSpacing: '-0.01em',
            }}
          >
            Log in
          </Typography>
          <Box
            aria-label="Jeff Says mark"
            sx={{
              width: 44,
              height: 44,
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 0.5,
              mt: 1,
            }}
          >
            <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: '#828282' }} />
            <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: '#828282' }} />
            <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: '#828282' }} />
            <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: 'primary.main' }} />
          </Box>
        </Box>

        {displayError && (
          <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
            {displayError}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="body2" sx={{ mb: 0.75, color: 'text.primary' }}>
              Email
            </Typography>
            <TextField
              required
              fullWidth
              id="email"
              name="email"
              autoComplete="email"
              autoFocus
              placeholder="example@gmail.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              inputProps={{ 'aria-label': 'Email' }}
            />
          </Box>

          <Box sx={{ mb: 1.5 }}>
            <Typography variant="body2" sx={{ mb: 0.75, color: 'text.primary' }}>
              Password
            </Typography>
            <PasswordTextField
              required
              fullWidth
              name="password"
              id="password"
              autoComplete="current-password"
              placeholder=""
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              inputProps={{ 'aria-label': 'Password' }}
            />
          </Box>

          <Box sx={{ textAlign: 'right', mb: 2 }}>
            <Typography
              variant="body2"
              component="span"
              sx={{
                color: 'rgba(0, 0, 0, 0.55)',
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' },
              }}
              onClick={() => {
                setForgotEmail(email);
                setForgotOpen(true);
              }}
            >
              Forgot password?
            </Typography>
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ py: 1.75, mb: 3 }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Log in'}
          </Button>

          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Link
              to={`/register${location.search}`}
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.7)' }}>
                Don&apos;t have an account?{' '}
                <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Sign up
                </Box>
              </Typography>
            </Link>
          </Box>

          <Box
            aria-label="Mascot placeholder"
            sx={{
              width: '100%',
              height: 280,
              display: { xs: 'block', sm: 'none' },
            }}
          />
        </Box>
      </Box>

      {/* Forgot Password Dialog */}
      <Dialog open={forgotOpen} onClose={handleForgotClose} maxWidth="xs" fullWidth>
        <DialogTitle>Forgot Password</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter the email address associated with your account. We&apos;ll send you a
            temporary password that you can use to log in and then change from your
            Profile page.
          </DialogContentText>

          {forgotSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {forgotSuccess}
            </Alert>
          )}
          {forgotError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {forgotError}
            </Alert>
          )}

          {!forgotSuccess && (
            <TextField
              autoFocus
              fullWidth
              label="Email address"
              type="email"
              value={forgotEmail}
              onChange={(e) => setForgotEmail(e.target.value)}
              disabled={forgotLoading}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleForgotClose} disabled={forgotLoading}>
            {forgotSuccess ? 'Close' : 'Cancel'}
          </Button>
          {!forgotSuccess && (
            <Button
              onClick={handleForgotPassword}
              variant="contained"
              disabled={forgotLoading}
            >
              {forgotLoading ? <CircularProgress size={20} /> : 'Send Temporary Password'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default LoginPage;
